'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { NormalFreeCreditsTaskName } from '@/app/constants';
import { useFreeCreditsTasks, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn, reportEvent, safeStorage } from '@/app/utils';

const DISMISSED_KEY = 'VIDU_UPGRADE_BANNER_DISMISSED';

/**
 * 任务列表顶部的会员引导条。
 * 广告覆盖地区（后端下发了看广告任务）引导去任务中心看广告赚积分，
 * 其他地区直接引导订阅 —— 两种地区能拿到的权益不同，文案与点击行为都不一样。
 */
export const UpgradeBanner = ({ className }: { className?: string }) => {
  const t = useTranslations();
  const userInfo = useStore((state) => state.userInfo);
  const { freeCreditsTasks, fetchFreeCreditTasks } = useFreeCreditsTasks();
  const { open: openTaskCenterDrawer } = useGlobalModal('taskCenterDrawer');
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const [dismissed, setDismissed] = useState(() => safeStorage.getItem(DISMISSED_KEY) === '1');

  // 任务列表可能还没拉过（横幅所在页面不一定打开过任务中心），这里补一次
  useEffect(() => {
    if (userInfo && freeCreditsTasks.length === 0) {
      fetchFreeCreditTasks();
    }
  }, [userInfo, freeCreditsTasks.length, fetchFreeCreditTasks]);

  // 看广告任务是否下发，等价于当前地区是否有广告覆盖
  const hasAdTask = useMemo(
    () => freeCreditsTasks.some((task) => task.name === NormalFreeCreditsTaskName.mission_watch_ad_credit),
    [freeCreditsTasks],
  );

  const isFree = userInfo?.subs_plan === 'free';

  const handleClick = () => {
    if (hasAdTask) {
      reportEvent('element_click', { element_name: 'upgrade_banner_ad', from_page: 'task_history' });
      openTaskCenterDrawer({ fromPage: 'create_page_banner' });
      return;
    }
    reportEvent('vip_price_exposure', { from_page: 'task_history_banner' });
    openPlanDetailDialog({ fromPage: 'task_history_banner' });
  };

  const handleDismiss = () => {
    safeStorage.setItem(DISMISSED_KEY, '1');
    setDismissed(true);
    reportEvent('element_click', { element_name: 'upgrade_banner_close', from_page: 'task_history' });
  };

  if (!isFree || dismissed) {
    return null;
  }

  return (
    <div
      className={cn(
        'flex w-full items-center gap-3 overflow-clip rounded-t-12 px-8 py-3 backdrop-blur-[6px]',
        'bg-[linear-gradient(90deg,rgba(224,221,255,0.12)_0%,rgba(148,216,255,0.12)_100%)]',
        className,
      )}
    >
      <span className="min-w-0 truncate font-normal text-base text-system-white">
        {hasAdTask ? t('upgradeBanner.adRegion') : t('upgradeBanner.default')}
      </span>
      <button
        type="button"
        onClick={handleClick}
        className="shrink-0 bg-ShengshuButton bg-clip-text font-semibold text-base text-transparent transition-opacity hover:opacity-80"
      >
        {hasAdTask ? t('upgradeBanner.adRegionAction') : t('upgradeBanner.defaultAction')}
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('payments.payResult.close')}
        className="ml-auto shrink-0 text-system-white opacity-60 transition-opacity hover:opacity-100"
      >
        <BaseLineClose className="text-xl" />
      </button>
    </div>
  );
};
